import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { LineChart, RefreshCw, Check, X, Lock, Trophy } from "lucide-react";
import { api } from "../../lib/api";
import { trackPrediction } from "../../lib/analytics";
import { useAuth } from "../../context/AuthContext";

const MARKET_ORDER = [
  "even_balls",
  "first_ball_scoring",
  "even_runs",
  "boundary",
  "six",
  "dot_balls_gt3",
  "wicket",
  "runs_gt7",
];

const LABELS = {
  even_balls: "01 · Double Runs",
  first_ball_scoring: "02 · 1st Ball Scores",
  even_runs: "03 · Even Over Runs",
  boundary: "04 · Boundary",
  six: "05 · Six",
  dot_balls_gt3: "06 · 3+ Dot Balls",
  wicket: "07 · Wicket",
  runs_gt7: "08 · 7+ Runs",
};

export default function PredictionsPanel({ matchId, overNumber }) {
  const { user, setAuthOpen } = useAuth();
  const [panel, setPanel] = useState(null);
  const [loading, setLoading] = useState(false);
  const [picks, setPicks] = useState({}); // { [market_id]: pickDoc }
  const [submitting, setSubmitting] = useState(null); // market_id being submitted

  const loadPanel = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/matches/${matchId}/predictions-panel`);
      setPanel(r.data);
    } finally {
      setLoading(false);
    }
  }, [matchId]);

  const loadMyPicks = useCallback(async () => {
    if (!user) { setPicks({}); return; }
    try {
      const r = await api.get(`/matches/${matchId}/market-predictions/me`);
      const map = {};
      for (const p of r.data.picks || []) {
        // Prefer the pick that matches the current upcoming over; otherwise keep previous-over pick to show result briefly
        const existing = map[p.market_id];
        if (!existing) { map[p.market_id] = p; continue; }
        if (p.over_number === overNumber) map[p.market_id] = p;
      }
      setPicks(map);
    } catch (_) {
      setPicks({});
    }
  }, [matchId, user, overNumber]);

  useEffect(() => { loadPanel(); }, [loadPanel, overNumber]);
  useEffect(() => { loadMyPicks(); }, [loadMyPicks]);

  const submitPick = async (market_id, answer) => {
    if (!user) { setAuthOpen(true); return; }
    if (picks[market_id]) return;
    setSubmitting(market_id);
    try {
      const r = await api.post(`/matches/${matchId}/market-predictions`, { market_id, answer });
      trackPrediction({ match_type: "demo", market: market_id, answer: String(answer) });
      setPicks((prev) => ({ ...prev, [market_id]: r.data }));
      toast.success(`Locked ${answer} on ${LABELS[market_id].split("·")[1].trim()}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not lock pick");
    } finally {
      setSubmitting(null);
    }
  };

  const cards = panel?.predictions || [];
  const byId = Object.fromEntries(cards.map((c) => [c.id, c]));
  const ordered = MARKET_ORDER.map((k) => byId[k]).filter(Boolean);

  return (
    <div className="tile p-6 grain" data-testid="predictions-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <LineChart className="w-4 h-4 text-amber-400" />
          <div>
            <div className="section-label">Live probability board · pick Yes/No · +5 pts each</div>
            <div className="font-display font-black uppercase text-lg tracking-tight">Predictions Panel</div>
          </div>
        </div>
        <button
          onClick={loadPanel}
          disabled={loading}
          className="btn-ghost text-xs"
          data-testid="predictions-panel-refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 inline mr-1 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {loading && !panel ? (
        <div className="text-slate-400 text-sm py-6 text-center">Computing markets…</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 stagger">
          {ordered.map((p) => (
            <MarketCard
              key={p.id}
              p={p}
              label={LABELS[p.id]}
              pick={picks[p.id]}
              isSubmitting={submitting === p.id}
              onPick={(ans) => submitPick(p.id, ans)}
              signedIn={Boolean(user)}
            />
          ))}
        </div>
      )}

      {panel && (
        <div className="mt-4 flex items-center justify-between text-[11px] text-slate-500 font-mono-data">
          <span>Over #{panel.over_number} · {panel.source}</span>
          <span>{cards.length} markets</span>
        </div>
      )}
    </div>
  );
}

function MarketCard({ p, label, pick, isSubmitting, onPick, signedIn }) {
  const isYesLead = p.answer === "Yes";
  const pct = p.probability;
  const locked = Boolean(pick);
  const resolved = pick && pick.status !== "pending";
  const won = pick?.status === "won";

  return (
    <div
      className={`relative tile-elev p-4 overflow-hidden ${resolved ? (won ? "ring-1 ring-emerald-400/60" : "ring-1 ring-rose-500/40") : ""}`}
      data-testid={`market-card-${p.id}`}
    >
      <div className="flex items-start justify-between">
        <div className="section-label">{label}</div>
        {resolved && (
          <div className={`pill ${won ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40" : "bg-rose-500/15 text-rose-300 border border-rose-500/40"}`}>
            {won ? <><Trophy className="w-3 h-3" /> +{pick.points_earned}</> : "Missed"}
          </div>
        )}
      </div>
      <div className="text-sm text-slate-200 mt-1.5 leading-snug min-h-[2.5rem]">{p.question}</div>

      <div className="mt-3 flex items-center justify-between">
        <div className={`pill border ${isYesLead ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" : "bg-rose-500/15 text-rose-300 border-rose-500/40"}`}>
          {isYesLead ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />} Lead: {p.answer}
        </div>
        <div className="metric-value text-2xl text-white" data-testid={`market-prob-${p.id}`}>{pct}%</div>
      </div>

      <div className="mt-3 h-1.5 rounded-full bg-white/5 overflow-hidden">
        <div
          className={`h-full ${isYesLead ? "bg-gradient-to-r from-emerald-500 to-amber-400" : "bg-gradient-to-r from-rose-500 to-slate-500"}`}
          style={{ width: `${pct}%` }}
        />
      </div>

      {/* Yes / No pick controls */}
      <div className="mt-3 grid grid-cols-2 gap-1.5">
        <button
          type="button"
          onClick={() => onPick("Yes")}
          disabled={locked || isSubmitting}
          className={`text-xs font-bold uppercase tracking-wider py-2 rounded-lg border transition-all
            ${pick?.answer === "Yes"
              ? "bg-emerald-500/25 border-emerald-400 text-emerald-200 ring-1 ring-emerald-400/50"
              : "bg-emerald-500/5 border-emerald-500/25 text-emerald-300 hover:bg-emerald-500/15"}
            ${locked && pick?.answer !== "Yes" ? "opacity-40" : ""}
            ${locked ? "cursor-not-allowed" : ""}
          `}
          data-testid={`market-pick-${p.id}-yes`}
        >
          {pick?.answer === "Yes" && <Lock className="w-3 h-3 inline mr-1" />}Yes
        </button>
        <button
          type="button"
          onClick={() => onPick("No")}
          disabled={locked || isSubmitting}
          className={`text-xs font-bold uppercase tracking-wider py-2 rounded-lg border transition-all
            ${pick?.answer === "No"
              ? "bg-rose-500/25 border-rose-400 text-rose-200 ring-1 ring-rose-400/50"
              : "bg-rose-500/5 border-rose-500/25 text-rose-300 hover:bg-rose-500/15"}
            ${locked && pick?.answer !== "No" ? "opacity-40" : ""}
            ${locked ? "cursor-not-allowed" : ""}
          `}
          data-testid={`market-pick-${p.id}-no`}
        >
          {pick?.answer === "No" && <Lock className="w-3 h-3 inline mr-1" />}No
        </button>
      </div>

      {resolved && pick.correct_answer && (
        <div className="mt-2 text-[10px] font-mono-data text-slate-400">
          Actual: <span className={won ? "text-emerald-300" : "text-rose-300"}>{pick.correct_answer}</span>
        </div>
      )}

      {!signedIn && (
        <div className="mt-2 text-[10px] text-slate-500">Sign in to lock a pick</div>
      )}
    </div>
  );
}
